/**
 * Checkin API Service
 *
 * Serviço para operações de check-in via API.
 * Suporta check-in online/offline com sincronização em lote.
 *
 * @since 2025-12-17
 * @module tmf_checkin
 */

import { api } from './apiClient';
import type {
  Checkin,
  CheckinListParams,
  CheckinActiveParams,
  CreateCheckinData,
  VisitCheckoutData,
  BatchCheckinData,
  BatchCheckinResult,
  BatchValidationResult,
  CheckinStatistics,
  NearbyLocation,
  NearbySearchParams,
  PaginatedCheckinResponse,
  CheckinActiveResponse,
  CheckinSuspiciousResponse,
  CheckinTasksListResponse,
  CheckinExecutionReport,
} from '@/types/checkin';

// Alias para compatibilidade (tipo foi renomeado para VisitCheckoutData)
type CheckoutData = VisitCheckoutData;

// ============================================
// ENDPOINTS
// ============================================

const endpoints = {
  checkin: {
    // IMPORTANTE: URLs com trailing slash para evitar 307 redirect que perde o header Authorization
    list: '/checkin/',
    view: (id: number) => `/checkin/${id}`,
    create: '/checkin/',
    checkout: (id: number) => `/checkin/${id}/checkout`,
    delete: (id: number) => `/checkin/${id}`,
    active: '/checkin/active',
    suspicious: '/checkin/suspicious',
    statistics: '/checkin/statistics',
    batch: '/checkin/batch',
    batchValidate: '/checkin/batch/validate',
    // Tasks & Report
    tasks: (id: number) => `/checkin/${id}/tasks`,
    report: (id: number) => `/checkin/${id}/report`,
  },
  location: {
    nearby: '/checkin/nearby',
    search: '/checkin/search',
  },
  dashboard: {
    realtime: '/checkin/dashboard/realtime',
    stats: '/checkin/dashboard/stats',
  },
};

// ============================================
// CHECKIN CRUD
// ============================================

/**
 * Lista check-ins com filtros e paginação
 */
export async function listCheckins(
  params: CheckinListParams = {}
): Promise<PaginatedCheckinResponse> {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', String(params.page));
  if (params.page_size) queryParams.append('page_size', String(params.page_size));
  if (params.person_id) queryParams.append('personId', String(params.person_id));
  if (params.resource_id) queryParams.append('resourceId', String(params.resource_id));
  if (params.active !== undefined) queryParams.append('active', String(params.active));
  if (params.date_from) queryParams.append('dateFrom', params.date_from);
  if (params.date_to) queryParams.append('dateTo', params.date_to);
  if (params.search) queryParams.append('search', params.search);

  const queryString = queryParams.toString();
  const url = queryString ? `${endpoints.checkin.list}?${queryString}` : endpoints.checkin.list;

  return api.get<PaginatedCheckinResponse>(url);
}

/**
 * Busca um check-in por ID
 */
export async function getCheckin(id: number): Promise<Checkin> {
  return api.get<Checkin>(endpoints.checkin.view(id));
}

/**
 * Cria um novo check-in
 */
export async function createCheckin(data: CreateCheckinData): Promise<Checkin> {
  return api.post<Checkin>(endpoints.checkin.create, {
    resource_id: data.id_resource,
    location: {
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: data.accuracy,
    },
    observation: data.observation,
    nonce: data.nonce,
    device_id: data.device_id,
    offline_datetime: data.datetime_checkin,
  });
}

/**
 * Faz checkout de um check-in
 */
export async function checkout(id: number, data: CheckoutData): Promise<Checkin> {
  return api.post<Checkin>(endpoints.checkin.checkout(id), {
    checkin_id: id,
    location: data.latitude && data.longitude ? {
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: data.accuracy,
    } : undefined,
    observation: data.observation,
  });
}

/**
 * Faz checkout do check-in ativo (sem precisar do ID)
 */
export async function checkoutActive(data: CheckoutData): Promise<Checkin> {
  return api.post<Checkin>(`${endpoints.checkin.list}/checkout`, {
    location: data.latitude && data.longitude ? {
      latitude: data.latitude,
      longitude: data.longitude,
      accuracy: data.accuracy,
    } : undefined,
    observation: data.observation,
  });
}

/**
 * Deleta um check-in (admin only)
 */
export async function deleteCheckin(id: number): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(endpoints.checkin.delete(id));
}

// ============================================
// ACTIVE & SUSPICIOUS CHECKINS
// ============================================

/**
 * Response da API Python para check-in ativo
 */
interface ActiveCheckinApiResponse {
  checkin: Checkin | null;
  is_active: boolean;
}

/**
 * Busca check-in ativo (sem checkout)
 */
export async function getActiveCheckins(
  params: CheckinActiveParams = {}
): Promise<CheckinActiveResponse> {
  const queryParams = new URLSearchParams();

  if (params.person_id) queryParams.append('person_id', String(params.person_id));

  const queryString = queryParams.toString();
  const url = queryString ? `${endpoints.checkin.active}?${queryString}` : endpoints.checkin.active;

  // API Python retorna { checkin: ..., is_active: bool }
  const response = await api.get<ActiveCheckinApiResponse>(url);

  // Converter para formato esperado pelo frontend
  return {
    data: response.checkin ? [response.checkin] : [],
    is_active: response.is_active,
  };
}

/**
 * Busca check-ins com sincronização suspeita
 * (check-ins muito antigos ainda sem checkout)
 */
export async function getSuspiciousCheckins(
  maxHours: number = 48
): Promise<CheckinSuspiciousResponse> {
  return api.get<CheckinSuspiciousResponse>(`${endpoints.checkin.suspicious}?maxHours=${maxHours}`);
}

// ============================================
// STATISTICS
// ============================================

/**
 * Busca estatísticas de check-ins
 */
export async function getStatistics(
  params: CheckinListParams = {}
): Promise<CheckinStatistics> {
  const queryParams = new URLSearchParams();

  if (params.person_id) queryParams.append('personId', String(params.person_id));
  if (params.date_from) queryParams.append('dateFrom', params.date_from);
  if (params.date_to) queryParams.append('dateTo', params.date_to);

  const queryString = queryParams.toString();
  const url = queryString
    ? `${endpoints.checkin.statistics}?${queryString}`
    : endpoints.checkin.statistics;

  return api.get<CheckinStatistics>(url);
}

// ============================================
// BATCH SYNC (OFFLINE SUPPORT)
// ============================================

/**
 * Sincroniza múltiplos check-ins em lote
 *
 * Usado para sincronizar check-ins feitos offline.
 * Os check-ins são ordenados por datetime e processados atomicamente.
 *
 * @param data Dados do batch
 * @returns Resultado do processamento com status de cada item
 */
export async function batchSync(data: BatchCheckinData): Promise<BatchCheckinResult> {
  return api.post<BatchCheckinResult>(endpoints.checkin.batch, {
    checkins: data.checkins,
    auto_checkout: data.auto_checkout ?? true,
    continue_on_error: data.continue_on_error ?? true,
    validate_only: data.validate_only ?? false,
  });
}

/**
 * Valida um lote de check-ins sem criar (dry run)
 *
 * Útil para verificar se os check-ins estão válidos antes de sincronizar.
 */
export async function batchValidate(
  checkins: CreateCheckinData[]
): Promise<BatchValidationResult> {
  return api.post<BatchValidationResult>(endpoints.checkin.batchValidate, {
    checkins,
  });
}

// ============================================
// LOCATION SEARCH
// ============================================

/**
 * Busca locais (recursos) próximos à localização atual
 */
export async function getNearbyLocations(
  params: NearbySearchParams
): Promise<NearbyLocation[]> {
  const queryParams = new URLSearchParams();

  queryParams.append('latitude', String(params.latitude));
  queryParams.append('longitude', String(params.longitude));
  if (params.radius) queryParams.append('radius', String(params.radius));
  if (params.limit) queryParams.append('limit', String(params.limit));
  if (params.search) queryParams.append('search', params.search);

  return api.get<NearbyLocation[]>(`${endpoints.location.nearby}?${queryParams.toString()}`);
}

/**
 * Busca locais (recursos) por texto
 */
export async function searchLocations(
  search: string,
  limit: number = 20
): Promise<NearbyLocation[]> {
  const queryParams = new URLSearchParams();
  queryParams.append('search', search);
  queryParams.append('limit', String(limit));

  return api.get<NearbyLocation[]>(`${endpoints.location.search}?${queryParams.toString()}`);
}

// ============================================
// DASHBOARD (REALTIME)
// ============================================

/**
 * Estatísticas do dashboard de check-ins
 */
export interface DashboardStats {
  onlineCount: number;
  offlineCount: number;
  averageTimeInStore: string;
  totalTime: string;
  visitedStores: number;
  registeredStores: number;
  executedActivities: number;
}

/**
 * Dados de promotor com status de check-in
 */
export interface PromoterCheckinData {
  id: number;
  name: string;
  integrationCode: string | null;
  imagePath: string | null;
  accessProfile: string | null;
  clientGroup: string | null;
  region: string | null;
  segment: string | null;
  checkinId: number | null;
  checkinLocation: string | null;
  checkinAddress: string | null;
  checkinDatetime: string | null;
  checkoutDatetime: string | null;
  isOnline: boolean;
  timeInStore: string | null;
  visitsToday: number;
}

/**
 * Resposta completa do dashboard
 */
export interface DashboardResponse {
  stats: DashboardStats;
  promoters: PromoterCheckinData[];
  lastUpdate: string;
}

/**
 * Parâmetros de busca do dashboard
 */
export interface DashboardParams {
  search?: string;
  statusFilter?: 'online' | 'offline' | 'active' | 'finished';
  regionId?: number;
}

/**
 * Busca dados do dashboard em tempo real
 */
export async function getDashboardRealtime(
  params: DashboardParams = {}
): Promise<DashboardResponse> {
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.append('search', params.search);
  if (params.statusFilter) queryParams.append('status_filter', params.statusFilter);
  if (params.regionId) queryParams.append('region_id', String(params.regionId));

  const queryString = queryParams.toString();
  const url = queryString
    ? `${endpoints.dashboard.realtime}?${queryString}`
    : endpoints.dashboard.realtime;

  return api.get<DashboardResponse>(url);
}

/**
 * Busca apenas estatísticas do dashboard (mais leve)
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  return api.get<DashboardStats>(endpoints.dashboard.stats);
}

// ============================================
// TASKS & REPORT
// ============================================

/**
 * Busca as tarefas executadas durante um check-in
 */
export async function getCheckinTasks(checkinId: number): Promise<CheckinTasksListResponse> {
  return api.get<CheckinTasksListResponse>(endpoints.checkin.tasks(checkinId));
}

/**
 * Busca o relatório de execução de um check-in
 */
export async function getCheckinReport(checkinId: number): Promise<CheckinExecutionReport> {
  return api.get<CheckinExecutionReport>(endpoints.checkin.report(checkinId));
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Gera um nonce único para deduplicação de check-ins
 */
export function generateNonce(): string {
  return crypto.randomUUID();
}

/**
 * Calcula distância entre dois pontos em metros
 * Usando fórmula de Haversine
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Formata duração em minutos para string legível
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}min`;
}

/**
 * Formata distância em metros para string legível
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

// ============================================
// SCHEDULE (Plan)
// ============================================

export interface SchedulePerson {
  id: number;
  name: string;
  nickname?: string;
  document?: string;
  address?: {
    address?: string;
    city?: string;
    state?: string;
  };
}

export interface ScheduleItem {
  id: number;
  personId?: number;
  promoter?: { id: number; name: string };
  person?: SchedulePerson;
  datetimePlannedCheckin?: string;
  datetimePlannedCheckout?: string;
  datetimeCheckin?: string;
  datetimeCheckout?: string;
  status?: { id: number; code: string; name?: string; cssClass?: string };
  progressCode?: string;
  typeCode?: number;
  canCancel: boolean;
  hasRecurrence: boolean;
  observation?: string;
}

export interface ScheduleTeamGroup {
  promoterId: number;
  promoterName: string;
  items: ScheduleItem[];
}

export interface ScheduleListResult {
  my: ScheduleItem[];
  team: ScheduleTeamGroup[];
}

export async function listSchedule(dateMs: number, teamIds?: number[]): Promise<ScheduleListResult> {
  const params = new URLSearchParams();
  params.append('timestamp', String(dateMs));
  if (teamIds && teamIds.length > 0) {
    params.append('listPersonFilter', JSON.stringify(teamIds));
  }
  return api.get<ScheduleListResult>(`/checkin/schedule/?${params.toString()}`);
}

export async function moveSchedule(scheduleId: number, targetDate: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>(`/checkin/schedule/${scheduleId}/move`, {
    target_date: targetDate,
  });
}

export async function cancelSchedule(scheduleId: number, reason?: string): Promise<{ success: boolean }> {
  const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  return api.post<{ success: boolean }>(`/checkin/schedule/${scheduleId}/cancel${params}`);
}

// ============================================
// EXPORT DEFAULT OBJECT
// ============================================

export const checkinApi = {
  // CRUD
  list: listCheckins,
  get: getCheckin,
  create: createCheckin,
  checkout,
  checkoutActive,
  delete: deleteCheckin,
  // Active & Suspicious
  getActive: getActiveCheckins,
  getSuspicious: getSuspiciousCheckins,
  // Statistics
  getStatistics,
  // Batch
  batchSync,
  batchValidate,
  // Location
  getNearby: getNearbyLocations,
  searchLocations,
  // Dashboard
  getDashboardRealtime,
  getDashboardStats,
  // Tasks & Report
  getTasks: getCheckinTasks,
  getReport: getCheckinReport,
  // Schedule (Plan)
  listSchedule,
  moveSchedule,
  cancelSchedule,
  // Helpers
  generateNonce,
  calculateDistance,
  formatDuration,
  formatDistance,
};

export default checkinApi;
